// socket.service.ts
import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  private socket!: Socket;
  private onlineUsersSubject = new BehaviorSubject<string[]>([]);
  onlineUsers$ = this.onlineUsersSubject.asObservable();
  private currentUserId: string = localStorage.getItem('userId') || '';

  constructor() {
    this.initializeSocket();
    this.listenForUpdates();
  }

  private initializeSocket(): void {
    const token = localStorage.getItem('authToken') || '';

    this.socket = io('https://api.nexgencoaches.com', {
      auth: { token },
      transports: ['websocket'],
      // keep autoConnect, that’s fine
    });

    if (this.currentUserId) {
      this.join(this.currentUserId);
    }
  }

  /** 🔄 Call this whenever authToken changes (e.g. after login) */
  refreshAuthToken(): void {
    if (!this.socket) return;

    const token = localStorage.getItem('authToken') || '';
    // socket.io client allows updating auth before reconnect
    (this.socket as any).auth = { token };

    // Force a reconnect so backend re-runs auth and sets socket.userId
    if (this.socket.connected) {
      this.socket.disconnect();
    }
    this.socket.connect();
  }

  join(studentId: string): void {
    this.currentUserId = studentId;
    localStorage.setItem('userId', studentId);
    if (this.socket && this.socket.connected) {
      this.socket.emit('student_online', studentId);
    }
  }

  sendMessage(receiverId: string, message: string): void {
    if (this.socket && this.socket.connected) {
      this.socket.emit('sendMessage', { receiverId, message });
    }
  }

  markAsSeen(conversationId: string): void {
    if (this.socket && this.socket.connected) {
      this.socket.emit('markAsSeen', { conversationId });
    }
  }

  fetchMessages(conversationId: string, page: number = 1, limit: number = 20): void {
    if (this.socket && this.socket.connected) {
      this.socket.emit('fetchMessages', { conversationId, page, limit });
    }
  }

  fetchConversations(): void {
    if (this.socket && this.socket.connected) {
      this.socket.emit('fetchConversations');
    }
  }

  get Socket(): Socket {
    return this.socket;
  }

  private listenForUpdates(): void {
    if (!this.socket) return;

    this.socket.on('update_online_users', (users: string[]) => {
      this.onlineUsersSubject.next(users);
    });

    this.socket.on('messageSent', (message: any) => {
      console.log('Message sent confirmation:', message);
    });

    this.socket.on('newMessage', (message: any) => {
      console.log('New message received:', message);
    });

    this.socket.on('messagesFetched', (messages: any[]) => {
      console.log('Messages fetched:', messages);
    });

    this.socket.on('conversationsFetched', (conversations: any[]) => {
      console.log('Conversations fetched:', conversations);
    });

    this.socket.on('messageSeenUpdate', (data: any) => {
      console.log('Messages seen update:', data);
    });

    this.socket.on('errorMessage', (error: any) => {
      console.error('Socket error:', error);
    });

    this.socket.on('connect', () => {
      console.log('Socket connected');
      if (this.currentUserId) {
        this.join(this.currentUserId);
      }
      // 🔥 IMPORTANT: always ask backend for bot conversations on (re)connect
    this.socket.emit('bot:listConversations');
    });

    

    this.socket.on('disconnect', () => {
      console.log('Socket disconnected');
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
    }
  }
}
